import { html, css, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { icons } from "../icons.js";
import { executionsStore } from "../executions-store.ts";
import { hermesClient } from "../hermes-singleton.ts";
import { t } from "../i18n.ts";

type ConfirmKind = "invalidate" | "recreate-skills";

const OPTIMIZE_POLL_MS = 5000;
const OPTIMIZE_TIMEOUT_MS = 600_000;

@customElement("ead-pfm-interactive-learning-toggle")
export class EadPfmInteractiveLearningToggle extends LitElement {
  static styles = css`
    :host {
      display: inline-flex;
      justify-content: flex-end;
    }

    .icon-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      border-radius: 8px;
      border: 1px solid color-mix(in srgb, var(--border) 88%, transparent);
      background: color-mix(in srgb, var(--bg-elevated, #1b1f2a) 80%, transparent);
      color: var(--text-strong, #f4f4f5);
      cursor: pointer;
      padding: 0;
    }

    .icon-btn:hover:not(:disabled) {
      background: var(--bg-hover, #232733);
    }

    .icon-btn:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }

    .icon-btn > span {
      display: inline-flex;
      width: 16px;
      height: 16px;
    }

    .icon-btn svg {
      width: 16px;
      height: 16px;
      stroke: currentColor;
      stroke-width: 2;
      fill: none;
    }

    .icon-btn--learning-on {
      color: #c4b5fd;
      border-color: color-mix(in srgb, #a78bfa 52%, transparent);
      background: color-mix(in srgb, #6f3fb3 22%, transparent);
    }

    .icon-btn--learning-off {
      color: #71717a;
      border-color: color-mix(in srgb, #52525b 50%, transparent);
      background: color-mix(in srgb, #18181b 70%, transparent);
    }

    .learning-popover-anchor {
      position: relative;
      display: inline-flex;
    }

    .learning-popover {
      position: absolute;
      top: calc(100% + 8px);
      right: 0;
      z-index: 40;
      width: min(320px, calc(100vw - 48px));
      padding: 12px 14px;
      border-radius: 12px;
      border: 1px solid color-mix(in srgb, #a78bfa 35%, var(--border));
      background: color-mix(in srgb, var(--card, #161920) 94%, #1a1430 6%);
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45);
      text-align: left;
    }

    .learning-popover-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      font-weight: 700;
      margin-bottom: 8px;
    }

    .learning-popover-copy {
      font-size: 12px;
      line-height: 1.5;
      color: color-mix(in srgb, var(--text, #d4d4d8) 92%, transparent);
      margin-bottom: 10px;
    }

    .learning-popover-check {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      font-size: 12px;
      line-height: 1.45;
      cursor: pointer;
      user-select: none;
    }

    .learning-popover-check input {
      margin: 2px 0 0;
      flex-shrink: 0;
    }

    .learning-popover-hint {
      margin-top: 8px;
      font-size: 11px;
      color: var(--muted);
    }

    .learning-actions {
      margin-top: 12px;
      padding-top: 10px;
      border-top: 1px solid color-mix(in srgb, var(--border) 70%, transparent);
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: center;
    }

    .btn-action {
      padding: 6px 10px;
      border-radius: 8px;
      border: 1px solid color-mix(in srgb, #a78bfa 45%, transparent);
      background: color-mix(in srgb, #6f3fb3 18%, transparent);
      color: #e9e0ff;
      font-size: 11px;
      font-weight: 600;
      cursor: pointer;
    }

    .btn-action:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .btn-menu {
      padding: 4px 10px;
      border-radius: 8px;
      border: 1px solid color-mix(in srgb, var(--border) 80%, transparent);
      background: transparent;
      color: var(--muted);
      font-size: 14px;
      cursor: pointer;
      margin-left: auto;
    }

    .menu-anchor {
      position: relative;
      margin-left: auto;
    }

    .menu-panel {
      position: absolute;
      top: calc(100% + 4px);
      right: 0;
      z-index: 50;
      min-width: 190px;
      padding: 4px;
      border-radius: 8px;
      border: 1px solid color-mix(in srgb, var(--border) 78%, transparent);
      background: var(--card, #161920);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
    }

    .menu-item {
      display: block;
      width: 100%;
      padding: 7px 10px;
      border: none;
      border-radius: 6px;
      background: transparent;
      color: var(--text, #d4d4d8);
      font-size: 11px;
      text-align: left;
      cursor: pointer;
    }

    .confirm-backdrop {
      position: fixed;
      inset: 0;
      z-index: 5100;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      background: rgba(0, 0, 0, 0.58);
    }

    .warn-card {
      width: min(400px, calc(100vw - 32px));
      padding: 16px 18px;
      border-radius: 14px;
      border: 1px solid color-mix(in srgb, #a78bfa 35%, var(--border));
      background: var(--card, #161920);
    }

    .warn-card--danger {
      border-color: color-mix(in srgb, #ef4444 35%, var(--border));
    }

    .learning-popover-status {
      margin-top: 8px;
      font-size: 11px;
      color: var(--muted);
    }

    .learning-popover-status--error {
      color: #fca5a5;
    }

    .learning-popover-status--ok {
      color: #86efac;
    }

    .learning-popover-notice {
      margin-bottom: 10px;
      padding: 8px 10px;
      border-radius: 8px;
      border: 1px solid color-mix(in srgb, var(--border) 75%, transparent);
      background: color-mix(in srgb, var(--bg-elevated, #1b1f2a) 55%, transparent);
      font-size: 12px;
      line-height: 1.5;
      color: color-mix(in srgb, var(--text, #d4d4d8) 88%, transparent);
    }

    .learning-popover-notice-hint {
      margin-top: 6px;
      font-size: 11px;
      color: var(--muted);
    }
  `;

  @property({ type: String }) executionId = "";
  @property({ type: Boolean }) enabled = true;
  @property({ type: Boolean }) hasOwnPfmMap = false;
  @property({ type: Boolean }) runActive = false;
  @property({ type: Number }) pfmRevision = 0;

  @state() private _popoverOpen = false;
  @state() private _menuOpen = false;
  @state() private _confirm: ConfirmKind | null = null;
  @state() private _saving = false;
  @state() private _creatingSkills = false;
  @state() private _optimizing = false;
  @state() private _hasSkills = false;
  @state() private _error = "";
  @state() private _statusNote = "";

  private _optimizeBaselineRev = 0;
  private _optimizePollTimer: ReturnType<typeof setInterval> | null = null;

  private readonly _onWindowKeydown = (event: KeyboardEvent): void => {
    if (event.key !== "Escape") return;
    if (this._confirm) {
      this._confirm = null;
      return;
    }
    if (this._menuOpen) this._menuOpen = false;
    else if (this._popoverOpen) this._popoverOpen = false;
  };

  private readonly _onDocumentPointerDown = (event: PointerEvent): void => {
    if (this._confirm) return;
    if (!this._popoverOpen && !this._menuOpen) return;
    if (event.composedPath().includes(this)) return;
    this._menuOpen = false;
    this._popoverOpen = false;
  };

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("keydown", this._onWindowKeydown);
    document.addEventListener("pointerdown", this._onDocumentPointerDown, true);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("keydown", this._onWindowKeydown);
    document.removeEventListener("pointerdown", this._onDocumentPointerDown, true);
    this._stopOptimizePoll();
  }

  private _stopOptimizePoll(): void {
    if (this._optimizePollTimer) {
      clearInterval(this._optimizePollTimer);
      this._optimizePollTimer = null;
    }
  }

  private async _refreshMeta(): Promise<void> {
    const executionId = String(this.executionId || "").trim();
    if (!executionId) return;
    try {
      const data = await hermesClient.request<{ hasSkills?: boolean }>("pfm.skills.list", {
        executionId,
      });
      this._hasSkills = data?.hasSkills === true;
    } catch {
      this._hasSkills = false;
    }
  }

  private _togglePopover(event: Event): void {
    event.stopPropagation();
    this._error = "";
    this._statusNote = "";
    this._menuOpen = false;
    const next = !this._popoverOpen;
    this._popoverOpen = next;
    if (next) void this._refreshMeta();
  }

  private async _setEnabled(next: boolean): Promise<void> {
    const executionId = String(this.executionId || "").trim();
    if (!executionId || this._saving) return;
    if (next && !this.hasOwnPfmMap) {
      this._error = t("learning.noOwnMapYet");
      return;
    }
    if (next && this.runActive) {
      this._error = t("learning.finishRunToEnable");
      return;
    }
    if (!next && this.enabled) {
      this._openConfirm("invalidate");
      return;
    }
    await this._applyEnabled(next);
  }

  private async _applyEnabled(next: boolean): Promise<void> {
    const executionId = String(this.executionId || "").trim();
    if (next && !this.hasOwnPfmMap) {
      this._error = t("learning.noOwnMapYet");
      return;
    }
    this._saving = true;
    this._error = "";
    try {
      await executionsStore.setExecutionContinuousLearning(
        executionId,
        next,
        next ? undefined : "Excluded from interactive learning — run not worth keeping",
      );
      this.enabled = next;
      if (!next) this._hasSkills = false;
      void this._refreshMeta();
      this.dispatchEvent(
        new CustomEvent("continuous-learning-changed", {
          detail: { executionId, enabled: next },
          bubbles: true,
          composed: true,
        }),
      );
    } catch (err) {
      this._error = err instanceof Error ? err.message : "Could not save.";
    } finally {
      this._saving = false;
    }
  }

  private async _createSkillFiles(): Promise<void> {
    const executionId = String(this.executionId || "").trim();
    if (!executionId || this._creatingSkills) return;
    this._creatingSkills = true;
    this._confirm = null;
    this._error = "";
    try {
      await hermesClient.request("pfm.skills.create", { executionId });
      this._hasSkills = true;
      await this._refreshMeta();
    } catch (err) {
      this._error = err instanceof Error ? err.message : "Could not create skill files.";
    } finally {
      this._creatingSkills = false;
    }
  }

  private async _optimizeMindmap(): Promise<void> {
    const executionId = String(this.executionId || "").trim();
    if (!executionId || this._optimizing || this.runActive) return;
    this._optimizing = true;
    this._error = "";
    this._statusNote = t("learning.optimizing");
    this._optimizeBaselineRev = Math.max(0, Number(this.pfmRevision ?? 0));

    try {
      const resp = await hermesClient.request<{
        ok?: boolean;
        code?: string;
        message?: string;
      }>("pfm.optimizeMindmap", { executionId });

      if (!resp?.ok) {
        this._error = String(resp?.message || t("learning.optimizeFailed"));
        this._statusNote = "";
        return;
      }

      const code = String(resp.code || "");
      if (code === "queued" || code === "queued_deferred" || code === "throttled") {
        this._statusNote = t("learning.optimizeQueued");
        this.dispatchEvent(
          new CustomEvent("pfm-mindmap-optimize-requested", {
            detail: { executionId },
            bubbles: true,
            composed: true,
          }),
        );
        this._startOptimizePoll(executionId);
        return;
      }

      this._error = String(resp?.message || t("learning.optimizeFailed"));
      this._statusNote = "";
    } catch (err) {
      this._error = err instanceof Error ? err.message : t("learning.optimizeFailed");
      this._statusNote = "";
    } finally {
      if (!this._optimizePollTimer) {
        this._optimizing = false;
      }
    }
  }

  private _startOptimizePoll(executionId: string): void {
    this._stopOptimizePoll();
    const started = Date.now();
    this._optimizing = true;

    const tick = async (): Promise<void> => {
      if (Date.now() - started > OPTIMIZE_TIMEOUT_MS) {
        this._stopOptimizePoll();
        this._optimizing = false;
        this._error = t("learning.optimizeTimeout");
        this._statusNote = "";
        return;
      }
      await executionsStore.refresh(true);
      const ex = executionsStore.executions.find((e) => e.id === executionId);
      const rev = Math.max(0, Number(ex?.pfmRevision ?? 0));
      if (rev > this._optimizeBaselineRev) {
        this._stopOptimizePoll();
        this._optimizing = false;
        this._statusNote = t("learning.optimizeDone");
        this.pfmRevision = rev;
        this.dispatchEvent(
          new CustomEvent("pfm-mindmap-optimize-requested", {
            detail: { executionId, completed: true },
            bubbles: true,
            composed: true,
          }),
        );
      }
    };

    void tick();
    this._optimizePollTimer = setInterval(() => void tick(), OPTIMIZE_POLL_MS);
  }

  private _onCheckboxChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!this.hasOwnPfmMap) {
      input.checked = false;
      return;
    }
    if (!input.checked && this.enabled) {
      input.checked = true;
      this._openConfirm("invalidate");
      return;
    }
    void this._applyEnabled(input.checked);
  }

  private _openConfirm(kind: ConfirmKind): void {
    this._menuOpen = false;
    this._confirm = kind;
  }

  private _confirmPrimaryAction(): void {
    if (this._confirm === "invalidate") {
      void this._applyEnabled(false);
      this._confirm = null;
      return;
    }
    if (this._confirm === "recreate-skills") {
      void this._createSkillFiles();
    }
  }

  private _renderConfirmDialog(): ReturnType<typeof html> {
    if (!this._confirm) return html``;
    const isInvalidate = this._confirm === "invalidate";
    return html`
      <div class="confirm-backdrop" @click=${() => (this._confirm = null)}>
        <div
          class=${isInvalidate ? "warn-card warn-card--danger" : "warn-card"}
          role="alertdialog"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <h4 style="margin:0 0 8px;font-size:14px;">
            ${isInvalidate ? t("learning.confirmInvalidate") : t("learning.confirmRecreate")}
          </h4>
          <p style="margin:0;font-size:12px;line-height:1.5;color:var(--muted);">
            ${isInvalidate ? t("learning.confirmInvalidateCopy") : t("learning.confirmRecreateCopy")}
          </p>
          <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;">
            <button type="button" class="btn-action btn-action--ghost" @click=${() => (this._confirm = null)}>
              ${t("learning.cancel")}
            </button>
            <button
              type="button"
              class="btn-action"
              ?disabled=${this._saving || this._creatingSkills}
              @click=${() => this._confirmPrimaryAction()}
            >
              ${isInvalidate ? t("learning.markInvalid") : t("learning.recreate")}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderActions(on: boolean): ReturnType<typeof html> {
    if (!this.hasOwnPfmMap) return html``;
    const showOptimize = !this.runActive;
    const showCreate = on && !this._hasSkills && !this.runActive;
    const showMenu = this._hasSkills;

    if (!showOptimize && !showCreate && !showMenu) return html``;

    return html`
      <div class="learning-actions">
        ${showOptimize
          ? html`
              <button
                class="btn-action"
                type="button"
                ?disabled=${this._optimizing || this._saving}
                @click=${() => void this._optimizeMindmap()}
              >
                ${this._optimizing ? t("learning.optimizing") : t("learning.optimize")}
              </button>
            `
          : ""}
        ${showCreate
          ? html`
              <button
                class="btn-action"
                type="button"
                ?disabled=${this._creatingSkills}
                @click=${() => void this._createSkillFiles()}
              >
                ${this._creatingSkills ? t("learning.saving") : t("learning.createSkills")}
              </button>
            `
          : ""}
        ${showMenu
          ? html`
              <span class="menu-anchor">
                <button class="btn-menu" type="button" @click=${(e: Event) => {
                  e.stopPropagation();
                  this._menuOpen = !this._menuOpen;
                }}>
                  ⋯
                </button>
                ${this._menuOpen
                  ? html`
                      <div class="menu-panel" @click=${(e: Event) => e.stopPropagation()}>
                        <button type="button" class="menu-item" @click=${() => this._openConfirm("recreate-skills")}>
                          ${t("learning.recreateSkills")}
                        </button>
                      </div>
                    `
                  : ""}
              </span>
            `
          : ""}
      </div>
    `;
  }

  render() {
    const executionId = String(this.executionId || "").trim();
    if (!executionId) return html``;

    const ownMap = this.hasOwnPfmMap === true;
    const on = ownMap && this.enabled !== false;
    const cannotEnable = !ownMap || (this.runActive && !on);

    return html`
      ${this._renderConfirmDialog()}
      <span class="learning-popover-anchor">
        <button
          class=${on ? "icon-btn icon-btn--learning-on" : "icon-btn icon-btn--learning-off"}
          type="button"
          aria-expanded=${this._popoverOpen ? "true" : "false"}
          @click=${(e: Event) => this._togglePopover(e)}
        >
          <span>${icons.brain}</span>
        </button>
        ${this._popoverOpen
          ? html`
              <div class="learning-popover" role="dialog" @click=${(e: Event) => e.stopPropagation()}>
                <div class="learning-popover-title">
                  <span>${icons.brain}</span>
                  <span>${t("learning.title")}</span>
                </div>
                <div class="learning-popover-copy">${t("learning.copy")}</div>
                ${!ownMap
                  ? html`
                      <div class="learning-popover-notice" role="status">
                        ${t("learning.noOwnMapYet")}
                        <div class="learning-popover-notice-hint">${t("learning.noOwnMapYetHint")}</div>
                      </div>
                    `
                  : ""}
                <label class="learning-popover-check">
                  <input
                    type="checkbox"
                    .checked=${on}
                    ?disabled=${this._saving || cannotEnable}
                    @change=${(e: Event) => this._onCheckboxChange(e)}
                  />
                  <span>${t("learning.validLabel")}</span>
                </label>
                <div class="learning-popover-hint">
                  ${!ownMap
                    ? ""
                    : this.runActive && !on
                      ? t("learning.hintActive")
                      : t("learning.hintInactive")}
                </div>
                ${this._renderActions(on)}
                ${this._saving
                  ? html`<div class="learning-popover-status">${t("learning.saving")}</div>`
                  : this._statusNote
                    ? html`<div class="learning-popover-status learning-popover-status--ok">${this._statusNote}</div>`
                    : this._error
                      ? html`<div class="learning-popover-status learning-popover-status--error">${this._error}</div>`
                      : ""}
              </div>
            `
          : ""}
      </span>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-pfm-interactive-learning-toggle": EadPfmInteractiveLearningToggle;
  }
}
